import React from 'react';
import { Heart, Award, Target, Users, BookOpen, Clock, Shield, Eye } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { rescueImages } from '../mock';
import contentData from '../data/content.json';

const About = () => {
  const teamMembers = contentData.teamMembers;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-forest-green/90 to-forest-green/70 z-10"></div>
        <img
          src={rescueImages[0]}
          alt="About us"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold font-heading mb-4">
            Our Story
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            A decade of compassion, dedication, and transforming lives
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold font-heading text-forest-green mb-6">
                Who We Are
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
              At Animal Care India, we stand where pain meets hope.
For every injured animal lying unnoticed on the roadside…
For every abandoned voiceless kid waiting for a familiar voice that never returns…
For every silent, suffering animal who has no one to call their own
we show up.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
              What began in 2020 as a small effort to help injured street dogs has grown into a national movement of compassion. Today, we rescue, treat, rehabilitate, feed, vaccinate, and protect thousands of animals every year but at the heart of our work lies something deeper:
              the belief that every life deserves dignity.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Our shelters provide medical care for the wounded, comfort for the abandoned, and lifelong safety for animals who cannot return to the streets.
Our outreach teams work daily in communities educating, engaging, and empowering people to choose empathy.
Our volunteers stand on the frontlines, turning kindness into action. We are not just rescuing animals.
We are restoring faith in humanity, in community, and in the power of compassion.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
              With the support of people like you, we dream of a world where animals are not feared, ignored, or hurt… but understood, protected, and loved.
              Together, we can create that world one rescue, one meal, one act of kindness at a time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-sand-beige">
        <div className="container mx-auto px-4">
          {/* Mission & Vision */}
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
            <Card className="border-none shadow-lg bg-white h-full">
              <CardContent className="p-8 text-center flex flex-col h-full">
                <div className="w-16 h-16 bg-warm-amber rounded-full flex items-center justify-center mx-auto mb-6">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold font-heading text-forest-green mb-4">Our Mission</h3>
                <p className="text-gray-700 leading-relaxed flex-grow">
                  To create a kinder, more informed world for animals by spreading awareness, educating communities, and empowering individuals to take compassionate action. We work to make animal rights understandable, accessible, and a part of everyday choices.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white h-full">
              <CardContent className="p-8 text-center flex flex-col h-full">
                <div className="w-16 h-16 bg-forest-green rounded-full flex items-center justify-center mx-auto mb-6">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold font-heading text-forest-green mb-4">Our Vision</h3>
                <p className="text-gray-700 leading-relaxed flex-grow">
                  A future where every animal is treated with dignity, empathy, and respect not because it is expected, but because it is understood. We envision a society where awareness leads to accountability, and where informed citizens help shape a safer environment for all animals.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Values */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-heading text-forest-green mb-4">Our Values</h2>
            <div className="w-24 h-1 bg-warm-amber mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                icon: Heart,
                title: "Compassion",
                desc: "We believe in kindness that translates into action, no matter how small.",
                color: "bg-red-100",
                iconColor: "text-red-500",
                fill: true
              },
              {
                icon: BookOpen,
                title: "Education",
                desc: "Knowledge drives change. We focus on simplifying complex issues so more people can make informed decisions.",
                color: "bg-blue-100",
                iconColor: "text-blue-500"
              },
              {
                icon: Clock,
                title: "Consistency",
                desc: "Real impact comes from steady, persistent efforts conversations, awareness sessions, and continuous community engagement.",
                color: "bg-purple-100",
                iconColor: "text-purple-500"
              },
              {
                icon: Award,
                title: "Respect",
                desc: "Every life matters. We encourage mindful choices that reflect respect towards animals, humans, and nature.",
                color: "bg-yellow-100",
                iconColor: "text-yellow-600"
              },
              {
                icon: Users,
                title: "Community",
                desc: "Change isn't individual it's collective. We work with schools, neighborhood groups, and citizens to build a culture of empathy.",
                color: "bg-green-100",
                iconColor: "text-green-600"
              },
              {
                icon: Shield,
                title: "Responsibility",
                desc: "We promote accountable behavior by helping people understand the real-world consequences of their interactions with animals.",
                color: "bg-orange-100",
                iconColor: "text-orange-500"
              }
            ].map((value, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-xl transition-shadow bg-white">
                <CardContent className="p-6 text-center">
                  <div className={`w-12 h-12 ${value.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <value.icon className={`w-6 h-6 ${value.iconColor}`} fill={value.fill ? "currentColor" : "none"} />
                  </div>
                  <h4 className="text-xl font-bold font-heading text-forest-green mb-3">{value.title}</h4>
                  <p className="text-gray-600 text-sm">
                    {value.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-700">
              Dedicated professionals working tirelessly for animal welfare
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {teamMembers.map((member) => (
              <Card key={member.id} className="border-none shadow-lg bg-white overflow-hidden group">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500 
                    "
                    style={{ objectPosition: '50% 10%' }}
                  />
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold font-heading text-forest-green mb-2">{member.name}</h3>
                  <p className="text-warm-amber font-semibold mb-3">{member.role}</p>
                  <p className="text-gray-700 text-sm">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
